/**
 * The model of the core's generic « Configuration » tab
 * (`GenericConfigurationTab.tsx`, genericity plan §2, §2 bis, step 5,
 * 2026-10-24): the tab of every type whose app-type module contributes none
 * (Odoo keeps its own, `plugins/odoo`). Nothing here knows a type: the
 * sections come from the descriptor's components and the ROLES of their
 * fields (`componentRoles`, `fieldRoles`, `unclassifiedFields` of
 * `@internal/plugin-waadoo-config-common`), the plan, the locks, the
 * profiles and the peak from the engine the backend's `waadoo:env:configure`
 * runs — as the Odoo model (`plugins/odoo`, `odooConfiguration/
 * configuration.ts`) does over its own shape.
 *
 * EVERY CONFIGURABLE FIELD IS PLACED EXACTLY ONCE (`genericLayout`): a field
 * a section already holds (one field named by the resources of two
 * components) is not placed again, and a field no section takes goes to
 * « Autres réglages » — nothing configurable is hidden, nothing is shown
 * twice.
 */

import {
  evaluate,
  planWithDescriptor,
  truthy,
  type AppTypeDescriptor,
  type ConfigurationChange,
  type ConfigurationGrant,
  type DescriptorComponent,
  type FieldValue,
  type FieldValues,
  type PlanProblem,
  type SingleFieldRole,
} from '@internal/plugin-waadoo-config-common';
import { databaseFieldsOf, type DatabaseFields } from './databaseFields';

/** The fields of one component by role, as a section shows them. */
export type RoleFields = Partial<Record<SingleFieldRole, string>>;

/** One component of a section, with the fields it shows by role. */
export interface ComponentPart {
  component: DescriptorComponent;
  roles: RoleFields;
}

/** One volume: its field, the component owning it, those mounting it too. */
export interface GenericVolume {
  field: string;
  owner: DescriptorComponent;
  mountedBy: DescriptorComponent[];
}

/**
 * One section of the generic tab; `fields`, every field it shows, in its
 * order (some only while the autoscaling is on, `hpaVisible`).
 */
export type GenericSection = { fields: string[] } & (
  | (ComponentPart & {
      kind: 'app';
      /** The main component: unfolded; the others are folded, summarised. */
      main: boolean;
    })
  | {
      kind: 'group';
      /** `components[].group`. */
      group: string;
      /**
       * The table's rows; `table`, the roles of each one's columns — pods
       * (its replicas, else its switch), CPU, memory (`groupTableRoles`).
       */
      members: Array<ComponentPart & { table: SingleFieldRole[] }>;
      /** Their fields the table has no column for, as rows after it. */
      extra: string[];
    }
  | { kind: 'volumes'; volumes: GenericVolume[] }
  | (ComponentPart & {
      kind: 'database';
      /** The fields of the shared PostgreSQL section, when it can take them. */
      shared?: DatabaseFields;
    })
  | (ComponentPart & { kind: 'mariadb' })
  | (ComponentPart & { kind: 'cache' })
  | { kind: 'others' }
);

/** The roles each kind's section shows, in its order (`ROLE_KINDS`, roles.ts). */
const APP_ROLES: readonly SingleFieldRole[] = [
  'enabled',
  'cpuRequest',
  'cpuLimit',
  'memoryRequest',
  'memoryLimit',
  'replicas',
  'hpaEnabled',
  'hpaMin',
  'hpaMax',
  'hpaTargetCpu',
  'hpaScaleDownWindow',
];
const DATABASE_ROLES: readonly SingleFieldRole[] = [
  'enabled',
  'databaseMode',
  'databaseSync',
  'cpuRequest',
  'cpuLimit',
  'memoryRequest',
  'memoryLimit',
  'databaseSize',
];
const MARIADB_ROLES: readonly SingleFieldRole[] = [
  'enabled',
  'cpuRequest',
  'cpuLimit',
  'memoryRequest',
  'memoryLimit',
  'databaseSize',
];
const CACHE_ROLES: readonly SingleFieldRole[] = [
  'enabled',
  'cpuRequest',
  'cpuLimit',
  'memoryRequest',
  'memoryLimit',
];

/** The roles a group's table has a column for (pods, CPU, memory). */
const GROUP_TABLE_ROLES: readonly SingleFieldRole[] = [
  'replicas',
  'cpuRequest',
  'cpuLimit',
  'memoryRequest',
  'memoryLimit',
];

/** Whether the field is bounded by a range for `grant` (a number input). */
export function rangedFor(
  descriptor: AppTypeDescriptor,
  grant: ConfigurationGrant,
  id: string,
): boolean {
  return descriptor.fieldById.get(id)?.bounds[grant]?.kind === 'range';
}

/**
 * The fields a group's table shows of a member, by role: pods (its
 * replicas, else its switch), CPU and memory; the rest of its roles are
 * rows after the table.
 */
export function groupTableRoles(roles: RoleFields): SingleFieldRole[] {
  return roles.replicas === undefined && roles.enabled !== undefined
    ? ['enabled', ...GROUP_TABLE_ROLES.slice(1)]
    : [...GROUP_TABLE_ROLES];
}

/**
 * The sections of the generic tab, in this order: one per `app` component
 * (a `group` as one section where its first member is), the volumes, the
 * CloudNativePG component, the MariaDB one, each Valkey cache, then « Autres
 * réglages » — every configurable field in exactly one of them (header), a
 * section without any field omitted.
 * `grant`: the shared PostgreSQL section takes its numbers only bounded by a
 * range for the caller (its inputs are number inputs).
 * `hidden`: the fields whose `when` is false for the environment
 * (`hiddenFieldIds`: Wagtail's media volume with its media on S3), in no
 * section — never shown, so never changed: their stored value stays.
 */
export function genericLayout(
  descriptor: AppTypeDescriptor,
  grant: ConfigurationGrant,
  hidden: ReadonlySet<string> = new Set(),
): GenericSection[] {
  const configurable = new Set(
    descriptor.configurableFields
      .map(field => field.id)
      .filter(id => !hidden.has(id)),
  );
  const placed = new Set<string>();
  const rolesOf = (component: DescriptorComponent) =>
    descriptor.componentRoles.get(component.id)?.roles ?? {};
  /** The fields of `roles` among `order` no section holds yet, now held. */
  const take = (
    roles: RoleFields,
    order: readonly SingleFieldRole[],
  ): RoleFields => {
    const taken: RoleFields = {};
    for (const role of order) {
      const id = roles[role];
      if (id === undefined || !configurable.has(id) || placed.has(id)) {
        continue;
      }
      taken[role] = id;
      placed.add(id);
    }
    return taken;
  };
  const listed = (roles: RoleFields, order: readonly SingleFieldRole[]) =>
    order.flatMap(role => {
      const id = roles[role];
      return id === undefined ? [] : [id];
    });

  const apps = descriptor.components.filter(item => item.kind === 'app');
  const { mainComponent } = descriptor;
  const main =
    mainComponent?.kind === 'app' && mainComponent.group === undefined
      ? mainComponent
      : // No ungrouped component is the chart's main block (a wrapper
        // chart's paths, or the main one in a group): the first ungrouped
        // one is unfolded, so that the tab never opens with every section
        // folded.
        apps.find(item => item.group === undefined);

  const sections: GenericSection[] = [];
  const groups = new Set<string>();
  for (const component of apps) {
    const { group } = component;
    if (group === undefined) {
      const roles = take(rolesOf(component), APP_ROLES);
      sections.push({
        kind: 'app',
        component,
        roles,
        main: component === main,
        fields: listed(roles, APP_ROLES),
      });
      continue;
    }
    if (groups.has(group)) continue;
    groups.add(group);
    const members = apps
      .filter(item => item.group === group)
      .map(item => {
        const own = rolesOf(item);
        const table = groupTableRoles(own);
        const roles = take(own, [
          ...table,
          ...APP_ROLES.filter(role => !table.includes(role)),
        ]);
        return { component: item, roles, table };
      });
    const tableFields = members.flatMap(member =>
      listed(member.roles, member.table),
    );
    const extra = members.flatMap(member =>
      listed(
        member.roles,
        APP_ROLES.filter(role => !member.table.includes(role)),
      ),
    );
    sections.push({
      kind: 'group',
      group,
      members,
      extra,
      fields: [...tableFields, ...extra],
    });
  }

  const componentById = new Map(
    descriptor.components.map(item => [item.id, item]),
  );
  const volumes: GenericVolume[] = [];
  for (const owner of descriptor.components) {
    for (const id of descriptor.componentRoles.get(owner.id)?.volumes ?? []) {
      if (!configurable.has(id) || placed.has(id)) continue;
      placed.add(id);
      volumes.push({
        field: id,
        owner,
        mountedBy: (descriptor.fieldRoles.get(id)?.mountedBy ?? []).flatMap(
          other => {
            const found = componentById.get(other);
            return found ? [found] : [];
          },
        ),
      });
    }
  }
  if (volumes.length > 0) {
    sections.push({
      kind: 'volumes',
      volumes,
      fields: volumes.map(volume => volume.field),
    });
  }

  const database = descriptor.databaseComponent;
  if (database) {
    const roles = take(rolesOf(database), DATABASE_ROLES);
    const shared = databaseFieldsOf(descriptor);
    const sharedFits =
      shared !== undefined &&
      roles.enabled === undefined &&
      shared.mode === roles.databaseMode &&
      shared.sync === roles.databaseSync &&
      shared.cpuRequest === roles.cpuRequest &&
      shared.cpuLimit === roles.cpuLimit &&
      shared.memoryRequest === roles.memoryRequest &&
      shared.memoryLimit === roles.memoryLimit &&
      shared.size === roles.databaseSize &&
      [
        shared.cpuRequest,
        shared.cpuLimit,
        shared.memoryRequest,
        shared.memoryLimit,
        shared.size,
      ].every(id => rangedFor(descriptor, grant, id));
    sections.push({
      kind: 'database',
      component: database,
      roles,
      ...(sharedFits ? { shared } : {}),
      fields: listed(roles, DATABASE_ROLES),
    });
  }
  const mariadb = descriptor.mariadbComponent;
  if (mariadb) {
    const roles = take(rolesOf(mariadb), MARIADB_ROLES);
    sections.push({
      kind: 'mariadb',
      component: mariadb,
      roles,
      fields: listed(roles, MARIADB_ROLES),
    });
  }
  for (const cache of descriptor.components.filter(
    item => item.kind === 'valkey',
  )) {
    const roles = take(rolesOf(cache), CACHE_ROLES);
    sections.push({
      kind: 'cache',
      component: cache,
      roles,
      fields: listed(roles, CACHE_ROLES),
    });
  }

  // « Autres réglages »: the fields without a role (`unclassifiedFields`),
  // and any field with a role no section above shows.
  const others = descriptor.configurableFields
    .map(field => field.id)
    .filter(id => configurable.has(id) && !placed.has(id));
  sections.push({ kind: 'others', fields: others });
  // A section without a field says nothing: omitted (a Valkey of constant
  // resources, a component whose fields have no role).
  return sections.filter(section => section.fields.length > 0);
}

/** Whether the autoscaling's rows of a component are shown. */
export function hpaVisible(roles: RoleFields, values: FieldValues): boolean {
  return roles.hpaEnabled === undefined || values[roles.hpaEnabled] === true;
}

/**
 * One problem of a draft, as the generic tab says it: in the core's words
 * for the engine's own refusals — a locked field, its bounds, its allowed
 * values, a volume that would shrink —; a rule's refusal (`text`) in the
 * descriptor's French, said under the row of `field`, the field the engine
 * names or the first its test reads (`PlanProblem.fields`), its `fields`
 * marked invalid. Without `field`, said in the panel.
 */
export type GenericProblem =
  | { key: 'locked'; field: string }
  | {
      key: 'bounds';
      field: string;
      values: {
        min: string | number;
        max: string | number;
        step: string | number;
      };
    }
  | { key: 'values'; field: string; values: { allowed: string | number } }
  | { key: 'shrink'; field: string; values: { current: string | number } }
  | { key: 'text'; field?: string; fields: string[]; text: string };

const figure = (value: FieldValue | undefined): string | number =>
  typeof value === 'number' ? value : String(value);

/** An engine refusal in the generic tab's terms (`GenericProblem`). */
export function genericProblemOf(problem: PlanProblem): GenericProblem {
  const { key, field, params } = problem;
  if (field !== undefined) {
    switch (key) {
      case 'locked':
        return { key: 'locked', field };
      case 'bounds':
        return {
          key: 'bounds',
          field,
          values: {
            min: figure(params.min),
            max: figure(params.max),
            step: figure(params.step),
          },
        };
      case 'values':
        return {
          key: 'values',
          field,
          values: { allowed: figure(params.allowed) },
        };
      case 'growOnly':
        return {
          key: 'shrink',
          field,
          values: { current: figure(params.current) },
        };
      default:
        break;
    }
  }
  const fields = problem.fields ?? (field !== undefined ? [field] : []);
  const anchor = field ?? fields[0];
  return {
    key: 'text',
    ...(anchor !== undefined ? { field: anchor } : {}),
    fields,
    text: problem.text,
  };
}

/** The fields a problem marks invalid. */
export function problemFields(problem: GenericProblem): string[] {
  if (problem.key !== 'text') return [problem.field];
  return problem.field !== undefined && !problem.fields.includes(problem.field)
    ? [problem.field, ...problem.fields]
    : problem.fields;
}

export interface GenericPlan {
  desired: FieldValues;
  /** The engine's changes, in the descriptor's order. */
  changes: ConfigurationChange[];
  problems: GenericProblem[];
  /** What to send: the fields the caller changed, by id, as asked. */
  asked: Partial<FieldValues>;
}

/**
 * The engine's plan over a draft (`planWithDescriptor`, as
 * `waadoo:env:configure` runs it): the configurable fields of `draft` that
 * differ from `current` are what is asked; the rules derive the rest.
 */
export function planGenericDraft(input: {
  descriptor: AppTypeDescriptor;
  current: FieldValues;
  draft: FieldValues;
  grant: ConfigurationGrant;
}): GenericPlan {
  const { descriptor, current, draft, grant } = input;
  const asked: Partial<FieldValues> = {};
  for (const field of descriptor.configurableFields) {
    if (draft[field.id] !== current[field.id]) {
      asked[field.id] = draft[field.id];
    }
  }
  const plan = planWithDescriptor({
    descriptor,
    current,
    changes: asked,
    grant,
  });
  return {
    desired: plan.desired,
    changes: plan.changes,
    problems: plan.problemDetails.map(genericProblemOf),
    asked,
  };
}

/**
 * Whether a folded section is unfolded: always while one of its fields has
 * a problem — folded, « Appliquer » would stay disabled for a reason the
 * reader could not see —, else as the reader last toggled it, else open when
 * one of its fields is changed or differs from the reference (`byDefault`).
 * The rule of the Odoo tab's « Haute disponibilité » (`haSectionOpen`).
 */
export function sectionOpen(input: {
  problems: boolean;
  toggled: boolean | undefined;
  byDefault: boolean;
}): boolean {
  return input.problems || (input.toggled ?? input.byDefault);
}

/** What a folded component's summary says: its pods, CPU and memory. */
export interface ComponentFigures {
  /** Its `enabled` is false: nothing else said. */
  disabled: boolean;
  /** A count, or the autoscaling's minimum and maximum. */
  pods: number | { min: number; max: number } | undefined;
  /** Per pod, millicores and Mi; undefined when it declares none. */
  cpu?: number;
  memory?: number;
}

const numberIn = (value: FieldValue | undefined): number | undefined =>
  typeof value === 'number' ? value : undefined;

/** The figures of a component over `values` (the plan's `desired`). */
export function componentFigures(
  part: ComponentPart,
  values: FieldValues,
): ComponentFigures {
  const { component, roles } = part;
  const context = { values };
  const resource = (key: 'cpuRequest' | 'memoryRequest') => {
    const ref = component.resources[key];
    return typeof ref === 'string' ? numberIn(values[ref]) : ref;
  };
  const disabled =
    component.enabled !== undefined &&
    !truthy(evaluate(component.enabled, context));
  let pods: ComponentFigures['pods'];
  const min = roles.hpaMin === undefined ? undefined : values[roles.hpaMin];
  const max = roles.hpaMax === undefined ? undefined : values[roles.hpaMax];
  if (
    roles.hpaEnabled !== undefined &&
    values[roles.hpaEnabled] === true &&
    typeof min === 'number' &&
    typeof max === 'number'
  ) {
    pods = { min, max };
  } else if (roles.replicas !== undefined) {
    pods = numberIn(values[roles.replicas]);
  } else {
    pods = numberIn(evaluate(component.pods, context));
  }
  const cpu = resource('cpuRequest');
  const memory = resource('memoryRequest');
  return {
    disabled,
    pods,
    ...(cpu !== undefined ? { cpu } : {}),
    ...(memory !== undefined ? { memory } : {}),
  };
}
